import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTvMode } from "@/context/TvModeContext";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe';

function getFocusables() {
  return Array.from(document.querySelectorAll(FOCUSABLE)).filter((el) => {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return false;
    const s = getComputedStyle(el);
    if (el.hasAttribute("data-spatial-skip")) return false;
    return s.visibility !== "hidden" && s.display !== "none" && s.opacity !== "0";
  });
}

function pickInDirection(current, list, key) {
  const cr = current.getBoundingClientRect();
  const cx = cr.left + cr.width / 2;
  const cy = cr.top + cr.height / 2;
  let best = null;
  let bestScore = Infinity;
  for (const el of list) {
    if (el === current) continue;
    const r = el.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    const dx = x - cx;
    const dy = y - cy;
    let primary;
    let secondary;
    let valid = false;
    if (key === "ArrowRight") { valid = dx > 8; primary = dx; secondary = Math.abs(dy); }
    else if (key === "ArrowLeft") { valid = dx < -8; primary = -dx; secondary = Math.abs(dy); }
    else if (key === "ArrowDown") { valid = dy > 8; primary = dy; secondary = Math.abs(dx); }
    else if (key === "ArrowUp") { valid = dy < -8; primary = -dy; secondary = Math.abs(dx); }
    if (!valid) continue;
    const score = primary + secondary * 2;
    if (score < bestScore) { bestScore = score; best = el; }
  }
  return best;
}

const ROW = ".overflow-x-auto";
const rowMemory = new WeakMap();

const CARD = ".rv-card";

// Left/Right inside a row: step exactly one card (DOM order), never a free scroll.
function siblingCard(current, key) {
  if (key !== "ArrowLeft" && key !== "ArrowRight") return null;
  const row = current.closest(ROW);
  if (!row || !current.matches(CARD)) return null;
  const cards = Array.from(row.querySelectorAll(CARD));
  const i = cards.indexOf(current);
  return cards[i + (key === "ArrowRight" ? 1 : -1)] || null;
}

function focusEl(el, horizontal = false) {
  if (!el) return;
  try { el.focus({ preventScroll: true }); } catch { el.focus(); }
  el.scrollIntoView({ block: horizontal ? "nearest" : "center", inline: "center", behavior: "smooth" });
  const row = el.closest(ROW);
  if (row) rowMemory.set(row, el);
}

// Entering a different row vertically → resume at the card last focused in that row.
function resumeInRow(current, next, list) {
  const row = next.closest(ROW);
  if (!row || row === current.closest(ROW)) return next;
  const saved = rowMemory.get(row);
  return saved && saved.isConnected && list.includes(saved) ? saved : next;
}

/** Hide the cursor while `active`; reveal on mouse move, re-hide after 3s idle. */
export function useIdleCursor(active) {
  useEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    let t;
    const hide = () => root.classList.add("cursor-hidden");
    const onMove = () => {
      root.classList.remove("cursor-hidden");
      clearTimeout(t);
      t = setTimeout(hide, 3000);
    };
    hide();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", hide);
    return () => {
      clearTimeout(t);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", hide);
      root.classList.remove("cursor-hidden");
    };
  }, [active]);
}

/** Global D-pad / remote spatial-navigation engine. Active only in TV mode. */
export const SpatialNav = () => {
  const { tvMode } = useTvMode();
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  useIdleCursor(tvMode);

  useEffect(() => {
    if (!tvMode) return undefined;

    const handler = (e) => {
      // Overlays that own their own key handling (e.g. Retro Mode) opt out of the global engine.
      if (document.querySelector("[data-spatial-trap]")) return;
      const active = document.activeElement;
      const tag = active && active.tagName;
      const inText = tag === "INPUT" || tag === "TEXTAREA";

      if (e.key === "Escape" || e.key === "GoBack" || e.key === "BrowserBack") {
        e.preventDefault();
        if (window.history.length > 1) navRef.current(-1);
        else navRef.current("/");
        return;
      }

      if (e.key === "Enter") {
        if (inText) return; // let inputs submit naturally
        if (active && active !== document.body) {
          e.preventDefault();
          active.click();
        }
        return;
      }

      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
      // Allow horizontal caret movement while typing.
      if (inText && (e.key === "ArrowLeft" || e.key === "ArrowRight")) return;

      e.preventDefault();
      const list = getFocusables();
      if (!list.length) return;

      if (!active || active === document.body || !list.includes(active)) {
        focusEl(list[0]);
        return;
      }
      // ArrowUp from the first content row jumps straight into the genre quick-filter bar.
      const bar = document.querySelector("[data-genre-bar]");
      const firstRow = document.querySelector(".rv-snap-row");
      if (e.key === "ArrowUp" && bar && firstRow && active && firstRow.contains(active)) {
        const pill = bar.querySelector('[aria-pressed="true"]') || bar.querySelector("button");
        if (pill) { focusEl(pill, true); return; }
      }
      const sib = siblingCard(active, e.key);
      if (sib) { focusEl(sib, true); return; }
      let next = pickInDirection(active, list, e.key);
      if (next && (e.key === "ArrowUp" || e.key === "ArrowDown")) next = resumeInRow(active, next, list);
      if (next) focusEl(next, e.key === "ArrowLeft" || e.key === "ArrowRight");
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [tvMode]);

  // On route change (in TV mode), land focus on the first actionable element.
  useEffect(() => {
    if (!tvMode) return undefined;
    const t = setTimeout(() => {
      const list = getFocusables();
      const active = document.activeElement;
      if (list.length && (!active || active === document.body || !list.includes(active))) {
        focusEl(list.find((el) => el.tagName !== "INPUT") || list[0]);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [tvMode, location.pathname]);

  return null;
};
